import { Card, Autocomplete, TextField, Checkbox } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../server";
import {
  useParams, useNavigate,
  useLocation
} from "react-router-dom";
import { RiStore3Fill, RiArrowLeftLine, RiUser3Fill, RiHistoryLine, RiFileList3Line, RiCalendarLine, RiExternalLinkLine, RiDeleteBin6Line } from "react-icons/ri";
import { Verified } from "@mui/icons-material";

export default function ShopDetails() {
  const location = useLocation();
  const shopId = location?.state?.shopId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shopDetails, setShopDetails] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Form state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [processingDD, setProcessingDD] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);

  const getShopDetailsgetById = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/shop/getById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: {
          shopId: shopId,
        },
      });
      setShopDetails(data?.data || data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.message || "Failed to fetch shop details");
    }
  };


  const getAllUsers = async () => {
    try {
      const { data } = await axios.get(`${server}/auth/getallUsers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: {
          pageNo: 1,
          pageSize: 1000000,
          verified:true
        }
      });
      setUsers(data?.data || []);
    } catch (e) {
      toast.error("Failed to fetch users");
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(
        `${server}/shop/addUsersIntoShop`,
        { userIdsList: selectedUsers.map(u => Number(u.id)) },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
          },
          params: {
            shopId: shopId,
          },
        }
      );
      toast.success(data?.message || "Users added successfully!");
      setSelectedUsers([]);
      getShopDetailsgetById();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add users");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveDD = async (ddId, approved) => {
    const action = approved ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${action} this Demand Draft?`)) {
      return;
    }
    try {
      setProcessingDD(ddId);
      const { data } = await axios.put(
        `${server}/dd/approve`,
        { ddId, approved },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
          },
        }
      );
      toast.success(data?.message || `DD ${approved ? "approved" : "rejected"} successfully!`);
      getShopDetailsgetById();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update DD status");
    } finally {
      setProcessingDD(null);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user from this shop?")) return;
    try {
      setRemovingUserId(userId);
      const { data } = await axios.put(
        `${server}/shop/removeUsersFromShop`,
        { userIdsList: [Number(userId)] },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
          },
          params: {
            shopId: shopId,
          },
        }
      );
      toast.success(data?.message || "User removed successfully!");
      getShopDetailsgetById();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to remove user");
    } finally {
      setRemovingUserId(null);
    }
  };

  useEffect(() => {
    getShopDetailsgetById();
    getAllUsers();
  }, [shopId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading shop details...</p>
        </div>
      </div>
    );
  }

  if (!shopDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <p className="text-slate-600">Shop not found</p>
          <button
            onClick={() => navigate("/my/shopList")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Shop List
          </button>
        </div>
      </div>
    );
  }

  // Combine users from different sources:
  // 1. Explicitly assigned users from shopDetails.userIds (matched against users list)

  const dDs = shopDetails?.dDs || [];
  const shopUserIds = shopDetails?.userIds || [];

  // Create a unique list of shop users
  const filteredShopUsers = users.filter(user =>
    shopUserIds.some(id => String(id) === String(user.id))
  );

  return (
    <div className="p-4 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate("/my/shopList")}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <RiArrowLeftLine className="text-2xl text-slate-700" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Shop Details</h1>
            <p className="text-slate-500 mt-1">Manage shop information and users</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Shop Details Card - Top Right */}
          <div className="lg:col-span-2 space-y-6">
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", p: 4 }}>
              <div className="flex items-start gap-5 mb-6">
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 shadow-sm">
                  <RiStore3Fill className="text-3xl text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-slate-800 tracking-tight">{shopDetails.shopName}</h2>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 shadow-sm">
                      {shopDetails.shopCode}
                    </span>
                    <button
                      onClick={() => navigate("/my/bidDetails", { state: { shopId: shopId } })}
                      className="flex items-center gap-2 px-4 py-1.5 bg-white border border-slate-200 hover:border-blue-500 text-slate-600 hover:text-blue-600 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <RiHistoryLine className="text-sm" />
                      History
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-200">
                <div>
                  <p className="text-sm text-slate-500">Shop Name</p>
                  <p className="font-semibold text-slate-800 mt-1">{shopDetails.shopName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Shop Code</p>
                  <p className="font-semibold text-slate-800 mt-1">{shopDetails.shopCode}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Highest Bid Amount</p>
                  <p className="font-semibold text-green-600 mt-1">₹{shopDetails.heighestAuctionBidAmount || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="font-semibold text-slate-800 mt-1">{filteredShopUsers.length}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <p className="font-semibold text-green-600 mt-1">Active</p>
                </div>
              </div>
            </Card>

            {/* Shop Users List */}
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", p: 3 }}>
              <h3 className="text-xl font-bold text-slate-800 mb-4">Shop Bidders</h3>
              {loadingUsers ? (
                <p className="text-slate-500 text-center py-4">Loading bidders...</p>
              ) : filteredShopUsers.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No bidders assigned to this shop yet</p>
              ) : (
                <div className="space-y-2">
                  {filteredShopUsers.map((user, index) => (
                    <div
                      key={user.id || index}
                      className="p-3 bg-slate-50 rounded-lg flex items-center gap-3"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <RiUser3Fill className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{user.name}</p>
                        <p className="text-sm text-slate-500">{user.userName}</p>
                      </div>
                      <div className="text-sm text-slate-500 mr-2">{user.mobile || "N/A"}</div>
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        disabled={removingUserId === user.id}
                        className={`p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ${removingUserId === user.id ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        title="Remove User"
                      >
                        <RiDeleteBin6Line className="text-lg" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Demand Drafts Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <RiFileList3Line className="text-blue-600" />
                  Demand Drafts
                </h3>
                <span className="px-3 py-1 bg-slate-200 text-slate-700 text-xs font-bold rounded-full">
                  {dDs.length} Total
                </span>
              </div>

              {dDs.length === 0 ? (
                <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", p: 4, textAlign: 'center' }}>
                  <p className="text-slate-500">No demand drafts found for this shop</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dDs.map((dd) => (
                    <Card key={dd.id} sx={{ borderRadius: 3, boxShadow: "0 4px 15px rgba(0,0,0,0.05)", p: 0, overflow: 'hidden', border: '1px solid', borderColor: 'slate.100' }}>
                      <div className="bg-slate-50 p-4 border-b border-slate-100 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <RiFileList3Line className="text-blue-600" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-bold text-slate-700">DD No: {dd.ddNo}</span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {dd.approved ? (
                                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-green-50 text-green-600 text-[9px] font-bold border border-green-100 uppercase tracking-tighter">
                                  <div className="w-1 h-1 rounded-full bg-green-500"></div>
                                  Approved
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[9px] font-bold border border-amber-100 uppercase tracking-tighter">
                                  <div className="w-1 h-1 rounded-full bg-amber-500"></div>
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!dd.approved && (
                            <button
                              onClick={() => handleApproveDD(dd.id, true)}
                              disabled={processingDD === dd.id}
                              className={`px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-[10px] font-bold rounded-lg transition-colors uppercase tracking-wider ${processingDD === dd.id ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            >
                              {processingDD === dd.id ? "..." : "Approve"}
                            </button>
                          )}
                          {dd.approved && (
                            <button
                              onClick={() => handleApproveDD(dd.id, false)}
                              disabled={processingDD === dd.id}
                              className={`px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 text-[10px] font-bold rounded-lg transition-colors uppercase tracking-wider ${processingDD === dd.id ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            >
                              {processingDD === dd.id ? "..." : "Reject"}
                            </button>
                          )}
                          {dd.ddCopy && (
                            <a
                              href={dd.ddCopy}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 p-1.5 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Document"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <RiUser3Fill className="text-slate-400 mt-1" />
                          <div>
                            <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Issued By</p>
                            <p className="text-sm font-semibold text-slate-800">{dd.name}</p>
                            <p className="text-xs text-slate-400">{dd.userName}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-50">
                          <div className="flex items-start gap-2">
                            <RiCalendarLine className="text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase font-bold">Issue Date</p>
                              <p className="text-xs font-semibold text-slate-700">{dd.issueDate}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <RiCalendarLine className="text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase font-bold">Expiry Date</p>
                              <p className="text-xs font-semibold text-orange-600">{dd.expiryDate}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side Column */}
          <div className="space-y-6">
            {/* Add User Form */}
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", p: 3 }}>
              <h3 className="text-xl font-bold text-slate-800 mb-4">Add Bidders to Shop</h3>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Bidders
                  </label>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={users.filter(user => 
                      !shopUserIds.some(id => String(id) === String(user.id))
                    )}
                    getOptionLabel={(option) => `${option.name} (${option.userName})`}
                    value={selectedUsers}
                    onChange={(event, newValue) => {
                      setSelectedUsers(newValue);
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700">{option.name}</span>
                          <span className="text-xs text-slate-400">{option.userName}</span>
                        </div>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search & Select Bidders..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#f8fafc',
                            borderRadius: '12px',
                            fontSize: '14px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || selectedUsers.length === 0}
                  className={`w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-200 active:scale-[0.98] ${submitting || selectedUsers.length === 0 ? "opacity-70 cursor-not-allowed shadow-none" : ""
                    }`}
                >
                  {submitting ? "Processing..." : "Assign Selected Bidders"}
                </button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
